import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";
import YAML from "yaml";
import { z } from "zod/v4";

import {
	type HttpRequestPage,
	HttpRequestPageSchema,
	getDefaultHttpRequestPage,
} from "@moment/api-collab";
import { type DocumentMetadata, DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { LOCAL_WORKSPACE_NAME } from "../constants";
import { env } from "../env";
import type { Template } from "../templates/templates.atom";
import { $internet } from "../utils";
import { type Workspace } from "../workspaces";
import {
	type DesktopAppDownloads,
	DesktopAppDownloadsSchema,
	type DocChangePermRequest,
	type DocPermission,
	type DocsApiWorkspace,
	type Invitation,
	InviteResultSchema,
	type MembershipResponse,
	MembershipResponseSchema,
	type PermRelation,
	type PermissionRolesResponse,
	PermissionsRolesResponseSchema,
	QueryDocumentsEntrySchema,
	type QueryDocumentsResponse,
	type Repo,
	type SearchUserResult,
	SearchUserResultSchema,
	type V2RepoGetResponse,
	V2RepoGetResponseSchema,
	type V2RepoResponse,
	V2RepoResponseSchema,
} from "./docsapi.types";

const logger = new Logger("docsapi");

export const CreateWorkspaceResponseSchema = z.object({
	workspaceID: z.string(),
	workspaceName: z.string(),
});

export type CreateWorkspaceResponse = z.infer<typeof CreateWorkspaceResponseSchema>;

export type CheckDocPermArgs = {
	workspaceName: string;
	documentId: string;
};

export type BaseUpdatePermArgs = {
	workspaceName: string;
	documentId: string;
	type: "grant" | "revoke";
	// Optional for revoke — omitting relation means "revoke all access".
	relation?: PermRelation;
};

export class DocsApiError extends Error {
	public resp?: Response;
	public error?: unknown;
	constructor(message: string, resp?: Response, error?: unknown) {
		super(message);
		this.name = "DocsApiError";
		this.resp = resp;
		this.error = error;
	}
}

export type DocsAPI = {
	document: {
		remote: {
			create: (
				workspaceName: string,
				documentId: string,
				token: string
			) => Promise<V2RepoResponse>;
			delete: (workspaceName: string, documentId: string, token: string) => Promise<void>;
			// for publish docs, token is optional.
			exists: (workspaceName: string, documentId: string, token?: string) => Promise<boolean>;
			get: (
				workspaceName: string,
				documentId: string,
				token?: string
			) => Promise<V2RepoGetResponse>;
		};
		permissions: {
			queryRolesForDocument: (
				args: Omit<CheckDocPermArgs, "permission">,
				token?: string
			) => Promise<PermissionRolesResponse>;
			queryUsersWithAccess: (
				args: CheckDocPermArgs & { permission: DocPermission },
				token?: string
			) => Promise<Array<SearchUserResult>>;
			queryInvites: (
				args: Omit<CheckDocPermArgs, "permission">,
				token?: string
			) => Promise<Array<Invitation>>;
			check: (
				args: CheckDocPermArgs & { permission: DocPermission; subject?: string },
				token?: string
			) => Promise<boolean>;
			checkMaxTableRead: (
				args: CheckDocPermArgs & { tableIds: string[]; subject?: string },
				token?: string
			) => Promise<string>;
			checkMaxTableWrite: (
				args: CheckDocPermArgs & { tableId: string; subject?: string },
				token?: string
			) => Promise<boolean>;
			updatePublicAccess: (
				opts: BaseUpdatePermArgs & { loggedInUsersOnly?: boolean },
				token: string
			) => Promise<void>;
			updateWorkspace: (opts: BaseUpdatePermArgs, token: string) => Promise<void>;
			updateInvite: (
				opts: BaseUpdatePermArgs & { email: string },
				token: string
			) => Promise<void>;
			update: (
				opts: BaseUpdatePermArgs & { user: SearchUserResult },
				token: string
			) => Promise<void>;
			publishWithPassword: (
				opts: { workspaceName: string; documentId: string; password: string },
				token: string
			) => Promise<void>;
		};
		metadata: {
			read: (
				input: {
					workspaceName: string;
					documentId: string;
					ref?: string;
				},
				token?: string
			) => Promise<DocumentMetadata>;
		};
		page: {
			readRaw: (
				input: {
					workspaceName: string;
					documentId: string;
					pageId: string;
					pagePath?: string;
					ref?: string;
				},
				token?: string
			) => Promise<string>;
			readHttpRequest: (
				input: {
					workspaceName: string;
					documentId: string;
					pageId: string;
					pagePath?: string;
					ref?: string;
				},
				token?: string
			) => Promise<HttpRequestPage>;
		};
		readRawFile: (
			input: {
				workspaceName: string;
				documentId: string;
				filePath: string;
				ref?: string;
			},
			token?: string
		) => Promise<string>;
		remoteMetadataExists: (
			input: {
				workspaceName: string;
				documentId: string;
			},
			token?: string
		) => Promise<boolean>;
		publish: {
			exchangePasswordForToken: (input: {
				workspaceName: string;
				documentId: string;
				password: string;
			}) => Promise<{ access_token: string; expires_in: number }>;
		};
	};
	invites: {
		accept: (inviteToken: string, token: string) => Promise<string>;
	};
	workspace: {
		exists: (workspaceName: string, token: string) => Promise<boolean>;
	};
	workspaces: {
		create: (
			name: string,
			token: string
		) => Promise<{ workspaceId: string; workspaceName: string }>;
		membership: {
			query: (workspace: Workspace, token?: string) => Promise<MembershipResponse>;
			remove: (workspace: Workspace, userIds: string[], token?: string) => Promise<void>;
			add: (workspace: Workspace, userIds: string[], token?: string) => Promise<void>;
		};
		queryDocuments: (workspace: Workspace, token?: string) => Promise<Array<Repo>>;
		list: (token: string) => Promise<Record<string, Workspace>>;
		uploadAvatar: (
			workspace: Workspace,
			file: File,
			token: string
		) => Promise<{ avatar_url: string }>;
		deleteAvatar: (workspace: Workspace, token: string) => Promise<void>;
	};
	payments: {
		createWorkspaceCheckout: (workspace: Workspace, token: string) => Promise<{ url: string }>;
		customer: {
			status: (workspace: Workspace, token: string) => Promise<{ status: string }>;
			sync: (workspace: Workspace, token: string) => Promise<void>;
		};
	};
	templates: {
		list: (token?: string, os?: string) => Promise<Template[]>;
	};
	users: {
		query: (query: string, token: string | undefined) => Promise<Array<SearchUserResult>>;
		get: (userId: string, token: string | undefined) => Promise<SearchUserResult>;
	};
	desktopApp: {
		downloads: () => Promise<DesktopAppDownloads>;
	};
	getRemoteUrl: (workspaceName: string, documentId: string, branch: string) => string;
	queryDocuments: (token?: string) => Promise<QueryDocumentsResponse>;
};

const createDocsAPI = (docsApiUrl: string): DocsAPI => {
	const callUpdatePermissions = async (
		{
			url,
			type,
			body,
		}: {
			url: string;
			type: "grant" | "revoke";
			body: DocChangePermRequest;
		},
		token?: string
	): Promise<void> => {
		const method = type === "grant" ? "POST" : "DELETE";

		const result = await sendRequest(
			url,
			{
				method,
				body: JSON.stringify(body),
			},
			token
		);
		if (result !== "OK") {
			throw new DocsApiError("Failed to change permission", result);
		}
	};

	const lookupMetadata = async (
		input: {
			workspaceName: string;
			documentId: string;
			ref?: string;
		},
		token?: string
	): Promise<Response> => {
		const { workspaceName, documentId, ref = "main" } = input;
		const response = await fetch(
			`${docsApiUrl}/api/v2/${workspaceName}/${documentId}/raw/moment.yml?ref=${ref}`,
			{
				headers: await buildRawRequestHeaders(undefined, token),
			}
		);

		// since we use the admin endpoint,
		// when we call this, this returns a 500.
		if (!response.ok) {
			// This is lame, but gitea sends a 500 when the document metadata is not found
			// using the admin token and the Sudo header.
			// this is lame. We can change this to a proxy call in docsapi if this is super annoying.
			if (response.status === 404 || response.status === 500) {
				throw new DocsApiError(
					`Document metadata not found for ${workspaceName}/${documentId}`,
					response
				);
			}

			logger.error("Failed to fetch document metadata from API", {
				workspaceName,
				documentId,
				response,
			});
			throw new Error("Failed to fetch document metadata from API");
		}
		return response;
	};

	const buildRawRequestHeaders = async (init?: HeadersInit, token?: string) => {
		const headers = new Headers(init);
		headers.set("Accept", "application/json");
		headers.set("Content-Type", "application/json");

		if (token && token !== "") {
			headers.set("Authorization", `Bearer ${token}`);
		} else {
			logger.debug("we are making an unauthenticated request");
		}

		return headers;
	};

	// we want the response object for permissions
	const sendRawRequest = async (path: string, init?: RequestInit, token?: string) => {
		const headers = await buildRawRequestHeaders(init?.headers, token);
		try {
			return await fetch(`${docsApiUrl}${path}`, {
				...init,
				headers,
			});
		} catch (e) {
			logger.error("Failed to fetch from docsapi", { path, init, error: e });
			if (e instanceof Error) {
				throw new DocsApiError(`${path} request failed. ${e.message}`, undefined, e);
			}
			throw new DocsApiError(`${path} request failed with unknown error`, undefined, e);
		}
	};

	const sendRequest = async (path: string, init?: RequestInit, token?: string) => {
		// TODO: add tracing headers here

		const headers = await buildRawRequestHeaders(init?.headers, token);
		try {
			const resp = await fetch(`${docsApiUrl}${path}`, {
				...init,
				headers,
			});

			if (!resp.ok) {
				logger.error(`Failed to fetch from docsapi: ${path}`, { path, init, resp });
				throw new DocsApiError(`${path} request failed. ${resp.statusText}`, resp);
			}
			return resp.json();
		} catch (e) {
			logger.error("Failed to fetch from docsapi", { path, init, error: e });
			if (e instanceof Error) {
				throw new DocsApiError(`${path} request failed. ${e.message}`, undefined, e);
			}
			throw new DocsApiError(`${path} request failed with unknown error`, undefined, e);
		}
	};

	const getEnv = () => ({
		giteaHostUrl: docsApiUrl,
	});

	return {
		document: {
			remote: {
				create: async (
					workspaceName: string,
					documentId: string,
					token: string
				): Promise<V2RepoResponse> => {
					const response = await sendRequest(
						`/internal/v2/repos/${encodeURIComponent(workspaceName)}`,
						{
							method: "POST",
							body: JSON.stringify({ documentID: documentId }),
						},
						token
					);

					return V2RepoResponseSchema.parse(response);
				},
				delete: async (workspaceName: string, documentId: string, token: string) => {
					if (!workspaceName || workspaceName === LOCAL_WORKSPACE_NAME) {
						throw new Error("Cannot delete document from local workspace");
					}
					// delete is a bit special, where we do not return any content, and just a 204.
					// thus we are going to write a custom fetch rather than using the docsApiFetch.

					const headers = await buildRawRequestHeaders(undefined, token);
					const path = `/internal/v1/repos/${encodeURIComponent(workspaceName)}/${encodeURIComponent(documentId)}`;

					try {
						const response = await fetch(`${docsApiUrl}${path}`, {
							headers,
							method: "DELETE",
						});
						if (!response.ok) {
							// Delete is intentionally idempotent: a missing repo (404)
							// means the desired end state already exists.
							if (response.status === 404) {
								return;
							}
							logger.error("Failed to delete remote document", {
								workspaceName,
								documentId,
								response,
							});
							throw new DocsApiError(
								`Failed to delete remote document from ${workspaceName}/${documentId}`,
								response
							);
						}
					} catch (e) {
						logger.error("Failed to delete remote document", {
							workspaceName,
							documentId,
							error: e,
						});
						let msg = `Failed to delete remote document from ${workspaceName}/${documentId}`;
						if (e instanceof Error) {
							msg += ` Error: ${e.message}`;
						}

						throw new DocsApiError(msg, undefined, e);
					}
				},
				exists: async (workspaceName: string, documentId: string, token?: string) => {
					if (!workspaceName) {
						return false;
					}

					const url = `${docsApiUrl}/api/v2/${workspaceName}/${documentId}/raw/moment.yml?ref=main`;
					logger.debug("Checking document exists via raw endpoint", {
						url,
						workspaceName,
						documentId,
					});

					let response: Response | undefined;
					try {
						response = await fetch(url, {
							headers: await buildRawRequestHeaders(undefined, token),
						});
					} catch (_e) {
						logger.debug("Document exists fetch threw", {
							url,
							error: _e instanceof Error ? _e.message : String(_e),
						});
						if (_e instanceof Error && _e.message === "Not found") {
							return false;
						}
						throw _e;
					}

					logger.debug("Document exists response", {
						url,
						status: response.status,
						ok: response.ok,
					});

					return response.ok;
				},
				get: async (
					workspaceName: string,
					documentId: string,
					token?: string
				): Promise<V2RepoGetResponse> => {
					const response = await sendRequest(
						`/internal/v2/repos/${encodeURIComponent(workspaceName)}/${encodeURIComponent(documentId)}`,
						{ method: "GET" },
						token
					);

					return V2RepoGetResponseSchema.parse(response);
				},
			},
			permissions: {
				queryRolesForDocument: async (
					{ workspaceName, documentId }: Omit<CheckDocPermArgs, "permission">,
					token?: string
				): Promise<PermissionRolesResponse> => {
					const data = await sendRequest(
						`/internal/v1/permissions/${workspaceName}/${documentId}/roles`,
						{ method: "GET" },
						token
					);

					try {
						const roles = PermissionsRolesResponseSchema.parse(data);
						return roles;
					} catch (e) {
						logger.error("Failed to parse queryRolesForDocument response", {
							workspaceName,
							documentId,
							error: e,
						});
						throw new DocsApiError(
							`Roles data was invalid for ${workspaceName}/${documentId}`,
							undefined,
							e
						);
					}
				},
				queryUsersWithAccess: async (
					{
						workspaceName,
						documentId,
						permission,
					}: CheckDocPermArgs & {
						permission: DocPermission;
					},
					token?: string
				): Promise<Array<SearchUserResult>> => {
					const params = new URLSearchParams();
					params.append("permission", permission);
					const data = await sendRequest(
						`/internal/v1/permissions/${workspaceName}/${documentId}/info?${params.toString()}`,
						{ method: "GET" },
						token
					);

					try {
						const parsed = z.array(SearchUserResultSchema).parse(data);
						return parsed;
					} catch (e) {
						logger.error("Failed to parse queryUsersWithAccess", {
							workspaceName,
							documentId,
							error: e,
						});
						throw new DocsApiError(
							`queryUsersWithAccess data was invalid for ${workspaceName}/${documentId}`,
							undefined,
							e
						);
					}
				},
				queryInvites: async (
					{ workspaceName, documentId }: Omit<CheckDocPermArgs, "permission">,
					token?: string
				): Promise<Array<Invitation>> => {
					const invites = await sendRequest(
						`/internal/v1/permissions/${workspaceName}/${documentId}/invites`,
						{
							method: "GET",
						},
						token
					);

					const schema = z.array(InviteResultSchema);
					const results = schema.parse(invites);
					return results;
				},
				check: async (
					{
						workspaceName,
						documentId,
						permission,
						subject,
					}: CheckDocPermArgs & {
						permission: DocPermission;
						subject?: string;
					},
					token?: string
				): Promise<boolean> => {
					const params = new URLSearchParams();
					params.append("permission", permission);
					subject && params.append("subject", subject);

					const url = `/internal/v1/permissions/${workspaceName}/${documentId}?${params.toString()}`;

					const response = await sendRawRequest(url, { method: "GET" }, token);

					if (response.ok) {
						return true;
					} else if (response.status === 403 || response.status === 404) {
						return false;
					}

					throw new DocsApiError(response.statusText, response);
				},
				checkMaxTableRead: async (
					{
						workspaceName,
						documentId,
						tableIds,
						subject,
					}: CheckDocPermArgs & {
						tableIds: string[];
						subject?: string;
					},
					token?: string
				): Promise<string> => {
					const params = new URLSearchParams();
					for (const tableId of tableIds) {
						params.append("tableId", tableId);
					}
					subject && params.append("subject", subject);
					const response = await sendRequest(
						`/internal/v1/permission/${workspaceName}/${documentId}/storage?${params.toString()}`,
						{ method: "GET" },
						token
					);
					return response;
				},
				checkMaxTableWrite: async (
					{
						workspaceName,
						documentId,
						tableId,
						subject,
					}: CheckDocPermArgs & {
						tableId: string;
						subject?: string;
					},
					token?: string
				): Promise<boolean> => {
					const params = new URLSearchParams();
					params.append("permission", "write_rows");
					subject && params.append("subject", subject);
					const response = await sendRawRequest(
						`/internal/v1/permission/${workspaceName}/${documentId}/storage/${tableId}?${params.toString()}`,
						{ method: "GET" },
						token
					);

					if (response.ok) {
						return true;
					} else if (response.status == 403 || response.status == 404) {
						return false;
					}

					throw new DocsApiError(response.statusText, response);
				},

				updatePublicAccess: async (
					opts: BaseUpdatePermArgs & { loggedInUsersOnly?: boolean },
					token: string
				) => {
					const { relation, loggedInUsersOnly, workspaceName, documentId } = opts;
					const body: DocChangePermRequest = {
						type: loggedInUsersOnly ? "LoggedInUsers" : "AllUsers",
						relation,
					};

					await callUpdatePermissions(
						{
							url: `/internal/v1/permissions/${workspaceName}/${documentId}?all=true`,
							body,
							...opts,
						},
						token
					);
				},

				updateWorkspace: async (opts: BaseUpdatePermArgs, token: string) => {
					const { relation, workspaceName, documentId } = opts;
					if (workspaceName === LOCAL_WORKSPACE_NAME) {
						throw new Error("Cannot update permissions for local workspace");
					}

					const body: DocChangePermRequest = {
						type: "Workspace",
						relation,
					};
					await callUpdatePermissions(
						{
							url: `/internal/v1/permissions/${workspaceName}/${documentId}`,
							body,
							...opts,
						},
						token
					);
				},

				updateInvite: async (
					opts: BaseUpdatePermArgs & { email: string },
					token: string
				) => {
					const { email, relation, workspaceName, documentId } = opts;
					if (relation === "owner") {
						throw new Error("Cannot invite a user to be an owner");
					}
					const body: DocChangePermRequest = {
						type: "Email",
						relation,
						value: email,
					};

					await callUpdatePermissions(
						{
							url: `/internal/v1/permissions/${workspaceName}/${documentId}`,
							body,
							...opts,
						},
						token
					);
				},

				update: async (
					opts: BaseUpdatePermArgs & { user: SearchUserResult },
					token: string
				) => {
					logger.debug("Updating user permission", { opts });
					const { user, relation, workspaceName, documentId } = opts;
					const body: DocChangePermRequest = {
						type: "Username",
						relation,
						value: user.nickname,
					};

					return await callUpdatePermissions(
						{
							url: `/internal/v1/permissions/${workspaceName}/${documentId}`,
							body,
							...opts,
						},
						token
					);
				},

				publishWithPassword: async (
					opts: { workspaceName: string; documentId: string; password: string },
					token: string
				) => {
					const { workspaceName, documentId, password } = opts;
					const response = await sendRawRequest(
						`/internal/v1/permissions/publish/password/${workspaceName}/${documentId}`,
						{
							method: "POST",
							body: JSON.stringify({ password }),
						},
						token
					);

					if (!response.ok) {
						throw new DocsApiError(
							`Failed to publish with password: ${response.statusText}`,
							response
						);
					}
				},
			},
			metadata: {
				read: async (
					input: { workspaceName: string; documentId: string; ref?: string },
					token?: string
				) => {
					const response = await lookupMetadata(input, token);

					const text = await response.text();
					const metadata = YAML.parse(text);

					return DocumentMetadataSchema.parse(metadata);
				},
			},

			page: {
				readRaw: async (
					input: {
						workspaceName: string;
						documentId: string;
						pageId: string;
						pagePath?: string;
						ref?: string;
					},
					token?: string
				) => {
					const { workspaceName, documentId, pageId, pagePath, ref = "main" } = input;
					const candidatePaths = [pagePath, `pages/${pageId}/index.md`].filter(
						(candidate, index, candidates): candidate is string => {
							return !!candidate && candidates.indexOf(candidate) === index;
						}
					);

					let lastResponse: Response | undefined;
					for (const rawPath of candidatePaths) {
						const path = `/api/v2/pierre/${workspaceName}/${documentId}/file?path=${encodeURIComponent(rawPath)}&ref=${encodeURIComponent(ref)}`;
						const response = await sendRawRequest(path, { method: "GET" }, token);
						if (response.ok) {
							return response.text();
						}

						lastResponse = response;
						if (response.status !== 404) {
							throw new DocsApiError(
								`Failed to fetch page: ${response.statusText}`,
								response
							);
						}
					}

					throw new DocsApiError(
						`Failed to fetch page: ${lastResponse?.statusText ?? "Not Found"}`,
						lastResponse
					);
				},
				readHttpRequest: async (
					input: {
						workspaceName: string;
						documentId: string;
						pageId: string;
						pagePath?: string;
						ref?: string;
					},
					token?: string
				): Promise<HttpRequestPage> => {
					const { workspaceName, documentId, pageId, pagePath, ref = "main" } = input;
					const candidatePaths = [pagePath, `pages/${pageId}/index.yml`].filter(
						(candidate, index, candidates): candidate is string => {
							return !!candidate && candidates.indexOf(candidate) === index;
						}
					);

					let lastResponse: Response | undefined;
					for (const rawPath of candidatePaths) {
						const path = `/api/v2/pierre/${workspaceName}/${documentId}/file?path=${encodeURIComponent(rawPath)}&ref=${encodeURIComponent(ref)}`;
						const response = await sendRawRequest(path, { method: "GET" }, token);
						if (response.ok) {
							const text = await response.text();
							if (text.trim().length === 0) {
								return getDefaultHttpRequestPage();
							}

							return HttpRequestPageSchema.parse(YAML.parse(text));
						}

						lastResponse = response;
						if (response.status !== 404) {
							throw new DocsApiError(
								`Failed to fetch HTTP request page: ${response.statusText}`,
								response
							);
						}
					}

					throw new DocsApiError(
						`Failed to fetch HTTP request page: ${lastResponse?.statusText ?? "Not Found"}`,
						lastResponse
					);
				},
			},

			readRawFile: async (
				input: {
					workspaceName: string;
					documentId: string;
					filePath: string;
					ref?: string;
				},
				token?: string
			): Promise<string> => {
				const { workspaceName, documentId, filePath, ref = "main" } = input;
				const encodedPath = filePath.split("/").map(encodeURIComponent).join("/");
				const path = `/api/v2/${workspaceName}/${documentId}/raw/${encodedPath}?ref=${ref}`;
				const response = await sendRawRequest(path, { method: "GET" }, token);
				if (!response.ok) {
					throw new DocsApiError(
						`Failed to fetch file: ${response.statusText}`,
						response
					);
				}
				return response.text();
			},

			// this might have happened when we successfully created a remote repo,
			// but failed to push.
			remoteMetadataExists: async (input, token?: string) => {
				if (!input.workspaceName) {
					return false;
				}

				try {
					const response = await lookupMetadata(input, token);
					if (!response.ok) {
						return false;
					}
					return true;
				} catch (_e) {
					// lookupMetadata should log when there is a real error and not a 404.
					// so going to just return false here.
					return false;
				}
			},

			publish: {
				exchangePasswordForToken: async (input: {
					workspaceName: string;
					documentId: string;
					password: string;
				}): Promise<{
					access_token: string;
					refresh_token?: string;
					expires_in: number;
					token_type?: string;
					scope?: string;
				}> => {
					const { workspaceName, documentId, password } = input;
					const response = await sendRawRequest(
						`/internal/v1/permissions/publish/exchange/${workspaceName}/${documentId}`,
						{
							method: "POST",
							body: JSON.stringify({ password }),
						}
					);
					if (!response.ok) {
						throw new DocsApiError("Invalid password", response);
					}
					return response.json();
				},
			},
		},

		invites: {
			accept: async (inviteToken: string, token: string): Promise<string> => {
				return await sendRequest(
					`/internal/v1/invite/accept`,
					{
						method: "POST",
						body: JSON.stringify({ inviteToken }),
					},
					token
				);
			},
		},

		workspace: {
			exists: async (workspaceName: string, token: string): Promise<boolean> => {
				const data = await sendRequest(
					`/internal/v1/workspace/exists/${workspaceName}`,
					{
						method: "GET",
					},
					token
				);
				const parsed = z.object({ status: z.enum(["AVAILABLE", "TAKEN"]) }).parse(data);
				return parsed.status === "TAKEN";
			},
		},

		workspaces: {
			create: async (
				name: string,
				token: string
			): Promise<{ workspaceId: string; workspaceName: string }> => {
				const resp = await sendRequest(
					"/internal/v1/workspaces",
					{
						method: "POST",
						body: JSON.stringify({ name }),
					},
					token
				);
				const parsed = CreateWorkspaceResponseSchema.parse(resp);
				return { ...parsed, workspaceId: parsed.workspaceID };
			},
			membership: {
				query: async (
					workspace: Workspace,
					token?: string
				): Promise<MembershipResponse> => {
					const data = await sendRequest(
						`/internal/v1/workspace/${encodeURIComponent(workspace.name)}/memberships`,
						{
							method: "GET",
						},
						token
					);

					return MembershipResponseSchema.parse(data);
				},
				remove: async (
					workspace: Workspace,
					userIds: string[],
					token?: string
				): Promise<void> => {
					if (isEmpty(userIds)) {
						return;
					}

					const resp = await sendRequest(
						`/internal/v1/workspace/${encodeURIComponent(workspace.name)}/memberships`,
						{
							method: "DELETE",
							body: JSON.stringify({ members: userIds }),
						},
						token
					);

					console.debug("successful", { resp });

					if (resp !== "OK") {
						logger.error("Failed to remove members from workspace", {
							resp,
							workspace,
							userIds,
						});
					}
				},
				add: async (
					workspace: Workspace,
					userIds: string[],
					token?: string
				): Promise<void> => {
					if (isEmpty(userIds)) {
						return;
					}

					const resp = await sendRequest(
						`/internal/v1/workspace/${encodeURIComponent(workspace.name)}/memberships`,
						{
							method: "POST",
							body: JSON.stringify({ members: userIds }),
						},
						token
					);
					if (resp !== "OK") {
						logger.error("Failed to add members to workspace", {
							resp,
							workspace,
							userIds,
						});
					}
				},
			},
			queryDocuments: async (workspace: Workspace, token?: string): Promise<Array<Repo>> => {
				if (isEmpty(workspace.name)) {
					return [];
				}

				const repos = await sendRequest(
					`/internal/v1/repos?workspace=${encodeURIComponent(workspace.name)}`,
					{
						method: "GET",
					},
					token
				);

				if (isEmpty(repos)) {
					return [];
				}

				return repos;
			},
			// personalWorkspace: async () => {
			// 	const authContext = store.get($authContext);
			// 	if (authContext.authnStatus !== "authenticated" || !authContext.user || authContext.user.nickname === undefined) {
			// 		console.debug("no authenticated user, cannot get personal workspace");
			// 		return undefined;
			// 	};

			// 	const giteaHost = getEnv().giteaHostUrl

			// 	const {
			// 		owned,
			// 	}: {
			// 		owned: Array<DocsApiWorkspace>;
			// 	} = await sendRequest(`/internal/v1/workspaces`, { method: "GET" });
			// 	const mappedOwned = new Map(
			// 		owned.map((workspace) => [workspace.display_name, workspace])
			// 	);

			// 	const personalWorkspace = mappedOwned.get(authContext.user.nickname);
			// 	if (!personalWorkspace) {
			// 		throw new DocsApiError(`Personal workspace not found for user ${authContext.user.nickname}`);
			// 	}

			// 	return {
			// 		id: personalWorkspace.id,
			// 		name: personalWorkspace.display_name,
			// 		host: giteaHost,
			// 		slug: personalWorkspace.sluggified_id,
			// 		owner: true,
			// 		role: "owner" as "owner" | "member" | "guest",
			// 	};
			// },
			list: async (token: string): Promise<Record<string, Workspace>> => {
				if (!token) {
					console.debug("unauthenticated, returning empty workspace list");
					return {};
				}

				const giteaHost = getEnv().giteaHostUrl;
				const {
					owned,
					member,
					guest,
				}: {
					owned: Array<DocsApiWorkspace>;
					member: Array<DocsApiWorkspace>;
					guest: Array<DocsApiWorkspace>;
				} = await sendRequest(`/internal/v1/workspaces`, { method: "GET" }, token);

				const mappedOwned: Record<string, Workspace> = Object.fromEntries(
					owned.map((workspace) => [
						workspace.id,
						{
							id: workspace.id,
							name: workspace.display_name,
							host: giteaHost,
							slug: workspace.sluggified_id,
							owner: true,
							role: "owner",
							paymentStatus: workspace.payment_status,
							avatarUrl: workspace.avatar_url,
						},
					])
				);

				const mappedMember: Record<string, Workspace> = Object.fromEntries(
					member
						.filter((w) => !mappedOwned[w.id])
						.map((workspace) => [
							workspace.id,
							{
								id: workspace.id,
								name: workspace.display_name,
								host: giteaHost,
								slug: workspace.sluggified_id,
								owner: false,
								role: "member",
								avatarUrl: workspace.avatar_url,
							},
						])
				);

				const mappedGuest: Record<string, Workspace> = Object.fromEntries(
					guest
						.filter((w) => !mappedOwned[w.id] && !mappedMember[w.id])
						.map((workspace) => [
							workspace.id,
							{
								id: workspace.id,
								name: workspace.display_name,
								host: giteaHost,
								slug: workspace.sluggified_id,
								owner: false,
								role: "guest",
								avatarUrl: workspace.avatar_url,
							},
						])
				);

				return {
					...mappedGuest,
					...mappedMember,
					...mappedOwned,
				};
			},
			uploadAvatar: async (
				workspace: Workspace,
				file: File,
				token: string
			): Promise<{ avatar_url: string }> => {
				const formData = new FormData();
				formData.append("avatar", file);

				const headers = await buildRawRequestHeaders(undefined, token);
				// Remove Content-Type so the browser sets it with the boundary
				headers.delete("Content-Type");

				const resp = await fetch(
					`${docsApiUrl}/internal/v1/workspace/${encodeURIComponent(workspace.name)}/avatar`,
					{
						method: "POST",
						headers,
						body: formData,
					}
				);

				if (!resp.ok) {
					throw new DocsApiError(`Failed to upload avatar: ${resp.statusText}`, resp);
				}

				return resp.json();
			},
			deleteAvatar: async (workspace: Workspace, token: string): Promise<void> => {
				const headers = await buildRawRequestHeaders(undefined, token);

				const resp = await fetch(
					`${docsApiUrl}/internal/v1/workspace/${encodeURIComponent(workspace.name)}/avatar`,
					{
						method: "DELETE",
						headers,
					}
				);

				if (!resp.ok) {
					throw new DocsApiError(`Failed to delete avatar: ${resp.statusText}`, resp);
				}
			},
		},

		payments: {
			createWorkspaceCheckout: async (
				workspace: Workspace,
				token: string
			): Promise<{ url: string }> => {
				if (!token) {
					throw new DocsApiError("Unauthenticated: cannot create workspace checkout");
				}
				// What is returned is a session. The backend will (if the workspace exists) and
				// there is no Stripe customer record, it will create the Stripe customer record
				// and return the URL for a checkout session.
				// If the Stripe customer already exists, it will return a link to the management
				// console for that workspace.
				const session = await sendRequest(
					`/payments/v1/stripe/checkout`,
					{
						method: "POST",
						body: JSON.stringify({ workspace_id: workspace.id }),
					},
					token
				);

				return session;
			},
			customer: {
				status: async (
					workspace: Workspace,
					token: string
				): Promise<{ status: string }> => {
					if (!token) {
						throw new DocsApiError("Unauthenticated: cannot get customer status");
					}
					try {
						const status = await sendRequest(
							`/payments/v1/stripe/customer/${workspace.id}/status`,
							{
								method: "GET",
							},
							token
						);
						return status;
					} catch (e) {
						if (e instanceof DocsApiError && e.resp?.status === 404) {
							return { status: "not_found" };
						}
						throw e;
					}
				},

				sync: async (workspace: Workspace, token?: string): Promise<void> => {
					if (!token) {
						throw new DocsApiError("Unauthenticated: cannot sync customer");
					}
					await sendRequest(
						`/payments/v1/stripe/customer/${workspace.id}/sync`,
						{
							method: "POST",
						},
						token
					);
				},
			},
		},

		templates: {
			list: async (token: string | undefined, os?: string) => {
				const params = new URLSearchParams();
				if (os) {
					params.set("os", os);
				}
				const query = params.toString();
				const path = query ? `/internal/v1/templates?${query}` : `/internal/v1/templates`;
				const templates: Template[] = await sendRequest(
					path,
					{
						method: "GET",
					},
					token
				);

				return templates;
			},
		},

		users: {
			query: async (
				query: string,
				token: string | undefined
			): Promise<Array<SearchUserResult>> => {
				if (token === "") {
					throw new DocsApiError("Unauthenticated: cannot query users");
				}
				const params = new URLSearchParams();
				params.append("q", query);

				const users = await sendRequest(
					`/internal/v1/query/users?${params.toString()}`,
					{
						method: "GET",
					},
					token
				);

				const parsed = z.array(SearchUserResultSchema).parse(users);
				return parsed;
			},
			get: async (userId: string, token: string | undefined): Promise<SearchUserResult> => {
				const user = await sendRequest(
					`/internal/v1/users/${encodeURIComponent(userId)}`,
					{ method: "GET" },
					token
				);

				const parsed = SearchUserResultSchema.parse(user);
				return parsed;
			},
		},

		desktopApp: {
			downloads: async (): Promise<DesktopAppDownloads> => {
				const response = await sendRequest(`/public/v1/desktopapp/download`, {
					method: "GET",
				});
				const parsed = DesktopAppDownloadsSchema.parse(response);
				return parsed;
			},
		},
		getRemoteUrl: (workspaceName: string, documentId: string, branch: string) => {
			return `${docsApiUrl}/api/v1/repos/${workspaceName}/${documentId}/archive/${branch}.tar.gz`;
		},
		queryDocuments: async (token?: string): Promise<QueryDocumentsResponse> => {
			const data = await sendRequest(`/internal/v1/querydocuments`, { method: "GET" }, token);

			return z.array(QueryDocumentsEntrySchema).parse(data);
		},
	};
};

const noOpDocsAPI: DocsAPI = {
	document: {
		remote: {
			create: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			delete: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			exists: async () => false,
			get: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
		permissions: {
			queryRolesForDocument: async () => ({ relation: {}, users: {} }),
			queryUsersWithAccess: async () => [],
			queryInvites: async () => [],
			check: async () => false,
			checkMaxTableRead: async () => "",
			checkMaxTableWrite: async () => false,
			updatePublicAccess: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			updateWorkspace: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			updateInvite: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			update: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			publishWithPassword: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
		metadata: {
			read: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
		page: {
			readRaw: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			readHttpRequest: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
		readRawFile: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
		remoteMetadataExists: async () => false,
		publish: {
			exchangePasswordForToken: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
	},
	invites: {
		accept: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
	},
	workspace: {
		exists: async () => false,
	},
	workspaces: {
		create: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
		membership: {
			query: async () => ({ relation: {}, users: {} }),
			remove: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
			add: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
		queryDocuments: async () => [],
		list: async () => ({}),
		uploadAvatar: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
		deleteAvatar: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
	},
	payments: {
		createWorkspaceCheckout: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
		customer: {
			status: async () => ({ status: "not_found" }),
			sync: async () => {
				throw new DocsApiError("DocsAPI not available");
			},
		},
	},
	templates: {
		list: async () => [],
	},
	users: {
		query: async () => [],
		get: async () => {
			throw new DocsApiError("DocsAPI not available");
		},
	},
	desktopApp: {
		downloads: async () => ({ urls: {} as any }),
	},
	getRemoteUrl: () => {
		throw new DocsApiError("DocsAPI not available");
	},
	queryDocuments: async () => [],
};

// DocsAPI atom
export const $docsApi = atom((get) => {
	// TODO!! make an error handler wrapper to
	// refresh internet if docsapi fails due to
	// no internet connection.
	// For safari, the error looks like
	// "The Internet connection appears to be offline."

	const isConnected = get($internet);

	if (env.docsApiUrl && isConnected) {
		return createDocsAPI(env.docsApiUrl);
	}

	return noOpDocsAPI;
});
$docsApi.debugLabel = "docsApi";
